import { Component, HostListener, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { I18nService } from '../../core/i18n.service';
import { SessionService } from '../../core/session.service';
import { ApiService } from '../../core/api.service';
import { PlanEntitlements, SubscriptionPlanCode } from '../../core/models';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { isTrustedHttpsHostUrl } from '../../core/url-security';

interface PlanViewModel {
  key: 'starter' | 'candidate' | 'researchPro';
  code: 'FREE' | 'BASE' | 'PRO';
  highlighted?: boolean;
  entitlements?: PlanEntitlements;
}

type BillingPeriodKey = 'monthly' | 'sixMonths' | 'yearly';

interface BillingOption {
  key: BillingPeriodKey;
  checkoutPeriod: 'monthly' | 'semiannual' | 'annual';
  months: number;
}

@Component({ selector: 'app-plans', standalone: true, imports: [FormsModule, RouterLink, LoadingSpinner], templateUrl: './plans.html', styleUrl: './plans.scss' })
export class Plans implements OnInit {
  readonly plans: PlanViewModel[] = [
    { key: 'starter', code: 'FREE' },
    { key: 'candidate', code: 'BASE', highlighted: true },
    { key: 'researchPro', code: 'PRO' }
  ];
  readonly billingOptions: BillingOption[] = [
    { key: 'monthly', checkoutPeriod: 'monthly', months: 1 },
    { key: 'sixMonths', checkoutPeriod: 'semiannual', months: 6 },
    { key: 'yearly', checkoutPeriod: 'annual', months: 12 }
  ];
  selectedPlan: PlanViewModel | null = null;
  selectedBillingPeriod: BillingPeriodKey = 'monthly';
  isPlanDetailClosing = false;
  private planDetailCloseTimer: ReturnType<typeof setTimeout> | null = null;
  currentEntitlements: PlanEntitlements | null = null;
  hasAcceptedTerms = false;
  hasAcceptedImmediatePerformance = false;
  isLoadingPlanCatalog = true;
  planCatalogLoadFailed = false;
  isStartingCheckout = false;
  isOpeningPortal = false;
  paymentActionMessageKey = '';

  constructor(public i18n: I18nService, private session: SessionService, private api: ApiService, private router: Router) {}

  async ngOnInit(): Promise<void> {
    await this.loadPlanCatalog();
  }

  get currentPlanKey(): PlanViewModel['key'] {
    const entitlementPlan = this.currentEntitlements?.plan;
    const entitlementKey = this.planKeyFromCode(entitlementPlan);
    if (entitlementKey) return entitlementKey;

    const tier = this.session.currentUser()?.planTier;
    return this.isPlanKey(tier) ? tier : 'starter';
  }

  planFeatures(planKey: PlanViewModel['key']): string[] {
    const features = this.i18n.value<string[]>(`plans.${planKey}.features`);
    return features ?? [];
  }

  planLimits(planKey: PlanViewModel['key']): string[] {
    const limits = this.i18n.value<string[]>(`plans.${planKey}.limits`);
    return limits ?? [];
  }

  pricingRationaleItems(): string[] {
    return this.i18n.value<string[]>('plans.pricingRationale.items') ?? [];
  }

  billingPrice(planKey: PlanViewModel['key'], period: BillingPeriodKey = this.selectedBillingPeriod): string {
    return this.i18n.t(`plans.${planKey}.billing.${period}.price`);
  }

  billingSavings(planKey: PlanViewModel['key'], period: BillingPeriodKey): string {
    return this.i18n.t(`plans.${planKey}.billing.${period}.savings`);
  }

  billingPeriodLabel(period: BillingPeriodKey): string {
    return this.i18n.t(`plans.billingPeriods.${period}`);
  }

  displayPrice(planKey: PlanViewModel['key']): string {
    const plan = this.findPlan(planKey);
    if (plan?.entitlements) return this.formatMonthlyPrice(plan.entitlements);
    return this.isPaidPlan(planKey) ? `${this.i18n.t(`plans.${planKey}.price`)} ${this.i18n.t('plans.perMonth')}` : this.i18n.t(`plans.${planKey}.price`);
  }

  displayMonthlyLimit(planKey: PlanViewModel['key'], field: keyof Pick<PlanEntitlements, 'monthlyCoverLetterGenerations' | 'monthlyDetailedFitReports' | 'monthlyProfileParagraphGenerations' | 'monthlyOfferComparisons'>): string {
    const entitlements = this.findPlan(planKey)?.entitlements;
    return String(entitlements?.[field] ?? '—');
  }

  displayStorageLimit(planKey: PlanViewModel['key']): string {
    const entitlements = this.findPlan(planKey)?.entitlements;
    return String(entitlements?.maxStoredFiles ?? '—');
  }

  isCurrentPlan(planKey: PlanViewModel['key']): boolean {
    if (!this.session.currentUser()) return false;
    return planKey === this.currentPlanKey;
  }

  isPaidPlan(planKey: PlanViewModel['key']): boolean {
    return planKey !== 'starter';
  }

  openPlanDetails(plan: PlanViewModel): void {
    if (this.planDetailCloseTimer) {
      clearTimeout(this.planDetailCloseTimer);
      this.planDetailCloseTimer = null;
    }
    this.isPlanDetailClosing = false;
    this.selectedPlan = plan;
    this.selectedBillingPeriod = 'monthly';
    this.resetCheckoutConsents();
  }

  closePlanDetails(): void {
    if (!this.selectedPlan || this.isPlanDetailClosing) return;
    this.isPlanDetailClosing = true;
    this.planDetailCloseTimer = setTimeout(() => {
      this.selectedPlan = null;
      this.isPlanDetailClosing = false;
      this.planDetailCloseTimer = null;
    }, 180);
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closePlanDetails();
  }

  selectBillingPeriod(period: BillingPeriodKey): void {
    this.selectedBillingPeriod = period;
    this.paymentActionMessageKey = '';
  }

  get hasCheckoutConsent(): boolean {
    return this.hasAcceptedTerms && this.hasAcceptedImmediatePerformance;
  }

  get isViewingCurrentPaidPlanPeriod(): boolean {
    if (!this.selectedPlan || !this.isPaidPlan(this.selectedPlan.key)) return false;
    return this.currentEntitlements?.plan === this.selectedPlan.code
      && this.normalizeBillingPeriod(this.currentEntitlements?.billingPeriod) === this.checkoutBillingPeriod(this.selectedBillingPeriod);
  }

  get isCheckoutDisabled(): boolean {
    return this.isStartingCheckout
      || this.isOpeningPortal
      || !this.hasCheckoutConsent
      || this.isViewingCurrentPaidPlanPeriod;
  }

  async startCheckout(): Promise<void> {
    if (!this.selectedPlan || !this.isPaidPlan(this.selectedPlan.key) || this.isStartingCheckout) return;
    if (this.isViewingCurrentPaidPlanPeriod) {
      this.paymentActionMessageKey = 'plans.alreadyCurrentPlanPeriod';
      return;
    }
    if (!this.hasCheckoutConsent) {
      this.paymentActionMessageKey = 'plans.consentRequired';
      return;
    }
    this.isStartingCheckout = true;
    this.paymentActionMessageKey = '';
    try {
      if (!(await this.session.ensureSession())) {
        await this.router.navigate(['/login'], { queryParams: { redirectTo: '/plans' } });
        return;
      }
      const response = await this.api.createCheckoutSession(this.selectedPlan.code, this.checkoutBillingPeriod(this.selectedBillingPeriod));
      if (!response.checkoutUrl) throw new Error('Checkout URL was not returned by the API.');
      if (!this.isTrustedStripeRedirectUrl(response.checkoutUrl, 'checkout.stripe.com')) throw new Error('Checkout URL was not issued by Stripe Checkout.');
      this.navigateToUrl(response.checkoutUrl);
    } catch (error) {
      console.warn('Unable to start checkout.', error);
      this.paymentActionMessageKey = 'plans.checkoutFailed';
    } finally {
      this.isStartingCheckout = false;
    }
  }

  async openCustomerPortal(): Promise<void> {
    if (this.isOpeningPortal) return;
    this.isOpeningPortal = true;
    this.paymentActionMessageKey = '';
    try {
      const response = await this.api.createCustomerPortalSession();
      if (!response.portalUrl) throw new Error('Portal URL was not returned by the API.');
      if (!this.isTrustedStripeRedirectUrl(response.portalUrl, 'billing.stripe.com')) throw new Error('Portal URL was not issued by Stripe Billing.');
      this.navigateToUrl(response.portalUrl);
    } catch (error) {
      console.warn('Unable to open customer portal.', error);
      this.paymentActionMessageKey = 'plans.portalFailed';
    } finally {
      this.isOpeningPortal = false;
    }
  }

  get canManageBilling(): boolean {
    return this.currentEntitlements?.billingManagementAvailable === true;
  }

  private async loadPlanCatalog(): Promise<void> {
    this.isLoadingPlanCatalog = true;
    this.planCatalogLoadFailed = false;
    try {
      const catalog = await this.api.listPlans();
      for (const plan of this.plans) {
        plan.entitlements = catalog.find(item => item.plan === plan.code);
      }
      if (await this.session.ensureSession()) {
        try {
          this.currentEntitlements = await this.api.getSubscriptionEntitlements();
        } catch (error) {
          console.warn('Unable to load current subscription entitlements.', error);
          this.currentEntitlements = null;
        }
      } else {
        this.currentEntitlements = null;
      }
    } catch (error) {
      console.warn('Unable to load plan catalog from API.', error);
      this.planCatalogLoadFailed = true;
    } finally {
      this.isLoadingPlanCatalog = false;
    }
  }

  private navigateToUrl(url: string): void {
    window.location.assign(url);
  }

  private isTrustedStripeRedirectUrl(url: string, expectedHost: string): boolean {
    return isTrustedHttpsHostUrl(url, expectedHost);
  }

  private formatMonthlyPrice(entitlements: PlanEntitlements): string {
    if (entitlements.priceEurMonthly === 0) return this.i18n.t('plans.starter.price');
    return `€${entitlements.priceEurMonthly.toFixed(2)} ${this.i18n.t('plans.perMonth')}`;
  }

  private findPlan(planKey: PlanViewModel['key']): PlanViewModel | undefined {
    return this.plans.find(plan => plan.key === planKey);
  }

  private resetCheckoutConsents(): void {
    this.hasAcceptedTerms = false;
    this.hasAcceptedImmediatePerformance = false;
    this.paymentActionMessageKey = '';
  }

  private checkoutBillingPeriod(period: BillingPeriodKey): BillingOption['checkoutPeriod'] {
    return this.billingOptions.find(option => option.key === period)?.checkoutPeriod ?? 'monthly';
  }

  private normalizeBillingPeriod(period: unknown): BillingOption['checkoutPeriod'] {
    const normalized = String(period ?? 'monthly').trim();
    if (normalized === 'annual' || normalized === 'yearly') return 'annual';
    if (normalized === 'semiannual' || normalized === 'sixMonths' || normalized === 'six-months') return 'semiannual';
    return 'monthly';
  }

  private isPlanKey(value: unknown): value is PlanViewModel['key'] {
    return value === 'starter' || value === 'candidate' || value === 'researchPro';
  }

  private planKeyFromCode(value: SubscriptionPlanCode | undefined): PlanViewModel['key'] | null {
    if (value === 'FREE') return 'starter';
    if (value === 'BASE') return 'candidate';
    if (value === 'PRO') return 'researchPro';
    return null;
  }
}
